// Light / dark theme control. The <html> `dark` class is what Tailwind's dark: variants key off.
// An inline script in public/index.html applies the saved theme before first paint (no flash);
// this module keeps it in sync afterwards and reacts to the OS setting while in 'auto'.
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

const STORAGE_KEY = 'theme'; // 'auto' | 'light' | 'dark'
const CHANGE_EVENT = 'themechange';
const REVEAL_MS = 700;

const systemQuery = () => window.matchMedia('(prefers-color-scheme: dark)');
const darkNow = () => document.documentElement.classList.contains('dark');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const getStoredTheme = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) || 'auto';
    } catch {
        return 'auto';
    }
};

const isDark = (mode) => mode === 'dark' || (mode === 'auto' && systemQuery().matches);

export const applyTheme = (mode) => {
    document.documentElement.classList.toggle('dark', isDark(mode));
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

// Point the reveal starts from: the clicked element's centre (works for mouse and keyboard)
export const originFromEvent = (event) => {
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null;
};

// Switch theme. With `origin`, the new theme spreads out in a circle from that point using the
// View Transitions API: the browser animates snapshots of the page, so it stays smooth however
// heavy the page is. Unsupported browsers and reduced-motion users get the instant switch.
export const setTheme = (mode, origin) => {
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // preference just won't persist
    }

    const changes = isDark(mode) !== darkNow();
    if (!changes || !origin || !document.startViewTransition || reducedMotion()) {
        applyTheme(mode);
        return;
    }

    const { x, y } = origin;
    const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
    // flushSync: React re-renders theme-aware components (e.g. the sun/moon icon) before the
    // browser captures the "new" snapshot
    const transition = document.startViewTransition(() => flushSync(() => applyTheme(mode)));
    transition.ready.then(() => {
        document.documentElement.animate(
            { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
            { duration: REVEAL_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', pseudoElement: '::view-transition-new(root)' }
        );
    });
};

let listening = false;

export const initTheme = () => {
    applyTheme(getStoredTheme());
    if (listening) return;
    listening = true;
    // Live-update only while the user is on 'auto'
    const onSystemChange = () => {
        if (getStoredTheme() === 'auto') applyTheme('auto');
    };
    systemQuery().addEventListener?.('change', onSystemChange);
};

// True while dark mode is showing; re-renders when the theme changes anywhere in the app
export const useIsDark = () => {
    const [dark, setDark] = useState(darkNow);
    useEffect(() => {
        const update = () => setDark(darkNow());
        window.addEventListener(CHANGE_EVENT, update);
        return () => window.removeEventListener(CHANGE_EVENT, update);
    }, []);
    return dark;
};
